import React from 'react';
import {Dimensions, View} from 'react-native';
import {RFValue} from 'react-native-responsive-fontsize';
import styled from 'styled-components/native';
import FastImage from 'react-native-fast-image';
import LinearGradient from 'react-native-linear-gradient';

import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {DefaultTheme} from 'styled-components';

export const CARD_WIDTH = Dimensions.get('window').width - 48;
export const NOTCH_R = 12;

// ─── Colors ──────────────────────────────────────────────────────────────────

function buildCategoryColors(theme: DefaultTheme) {
  return {
    ouro: {
      border: theme.colors.coupon_category_ouro_border,
      iconColor: theme.colors.coupon_category_ouro_icon,
      actionBtnBg: theme.colors.coupon_category_ouro_action_bg,
      actionBtnText: theme.colors.coupon_category_ouro_action_text,
    },
    prata: {
      border: theme.colors.coupon_category_prata_border,
      iconColor: theme.colors.coupon_category_prata_icon,
      actionBtnBg: theme.colors.coupon_category_prata_action_bg,
      actionBtnText: theme.colors.coupon_category_prata_action_text,
    },
    default: {
      border: theme.colors.coupon_category_default_border,
      iconColor: theme.colors.coupon_category_default_icon,
      actionBtnBg: theme.colors.coupon_category_default_action_bg,
      actionBtnText: theme.colors.coupon_category_default_action_text,
    },
  };
}

export function getCategoryTokens(categoria: string, theme: DefaultTheme) {
  const categoryColors = buildCategoryColors(theme);
  const key = categoria?.toLowerCase() as keyof typeof categoryColors;
  return categoryColors[key] ?? categoryColors.default;
}

export function getFuelCircleColor(fuelName: string): string {
  const n = fuelName?.toLowerCase() ?? '';
  if (n.includes('etanol')) {
    return '#1B5E20';
  }
  if (n.includes('aditivada')) {
    return '#0D47A1';
  }
  if (n.includes('diesel')) {
    return '#4E342E';
  }
  if (n.includes('gasolina')) {
    return '#2E5339';
  }
  return '#37474F';
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────

// Envolve o CardContainer: a opacidade do card expirado fica restrita a ele,
// permitindo que o overlay (fundo + conteúdo) fique fora do alcance dessa opacidade
export const CardOuter = styled.View`
  flex: 1;
  position: relative;
`;

// overflow:hidden aqui clipa os NotchCircles na borda externa do card (x=0)
export const CardContainer = styled(LinearGradient)<{expired: boolean}>`
  flex: 1;
  border-radius: ${RFValue(20)}px;
  overflow: hidden;
  opacity: ${({expired}) => (expired ? 0.65 : 1)};
`;

export const EXPIRING_TODAY_COLOR = '#E53935';

// Borda colorida: absolute, SEM overflow:hidden (z=1, fica atrás do ContentClip)
export const BorderRing = styled.View<{categoria: string; expiringToday?: boolean}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: ${RFValue(20)}px;
  border-width: 1.5px;
  border-color: ${({categoria, theme}) =>
   getCategoryTokens(categoria, theme).border};
`;

// Clip de conteúdo: overflow:hidden, SEM borda, inset de 1.5px (z=2, cobre a borda)
export const ContentClip = styled.View`
  margin: 1.5px;
  border-radius: ${RFValue(18.5)}px;
  overflow: hidden;
`;

export const CardTouchable = styled.TouchableOpacity`
  flex: 1;
`;

// ─── Top section ─────────────────────────────────────────────────────────────

export const TopSection = styled.View`
  padding: ${RFValue(14)}px ${RFValue(16)}px ${RFValue(12)}px;
`;

export const BadgesRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${RFValue(10)}px;
`;

export const CategoryBadge = styled.View<{categoria: string}>`
  border-radius: ${RFValue(6)}px;
  padding: ${RFValue(3)}px ${RFValue(8)}px;
  background-color: ${({categoria, theme}) =>
    categoria?.toLowerCase() === 'ouro'
      ? theme.colors.coupon_badge_ouro_bg
      : theme.colors.coupon_badge_prata_bg};
  border-width: 1px;
  border-color: ${({categoria, theme}) =>
    categoria?.toLowerCase() === 'ouro'
      ? theme.colors.coupon_badge_ouro_border
      : theme.colors.coupon_badge_prata_border};
`;

export const CategoryBadgeText = styled.Text<{categoria: string}>`
  font-size: ${RFValue(9)}px;
  font-family: ${({theme}) => theme.fonts.bold};
  letter-spacing: 1.2px;
  color: ${({categoria, theme}) =>
    categoria?.toLowerCase() === 'ouro'
      ? theme.colors.coupon_badge_ouro_text
      : theme.colors.coupon_badge_prata_text};
`;

export const FuelRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${RFValue(12)}px;
`;

export const FuelCircle = styled.View<{bg: string}>`
  width: ${RFValue(44)}px;
  height: ${RFValue(44)}px;

  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

export const FuelCircleImage = styled(FastImage)`
  width: ${RFValue(44)}px;
  height: ${RFValue(44)}px;
  border-radius: ${RFValue(12)}px;
`;

export const FuelCircleLetter = styled.Text`
  color: #ffffff;
  font-size: ${RFValue(22)}px;
  font-family: ${({theme}) => theme.fonts.bold};
`;

export const FuelInfo = styled.View`
  flex: 1;
`;

/* CORRIGIDO: era color: #1a1a1a (fixo) — ficava ilegível no dark mode.
   Agora usa theme.colors.title (dark: #fff / light: #222). */
export const FuelNameText = styled.Text`
  color: ${({theme}) => theme.colors.title};
  font-size: ${RFValue(12)}px;
  font-family: ${({theme}) => theme.fonts.bold};
`;

/* CORRIGIDO: era color: #635151 (fixo) — mesmo problema de contraste no dark mode.
   Agora usa theme.colors.text_2 (dark: #FFF / light: #797e91). */
export const CityText = styled.Text`
  color: ${({theme}) => theme.colors.text_2};
  font-size: ${RFValue(11)}px;
  font-family: ${({theme}) => theme.fonts.regular};
`;

// ─── Divider / notch ─────────────────────────────────────────────────────────

export const DividerRow = styled.View`
  flex-direction: row;
  align-items: center;
  height: ${RFValue(NOTCH_R)}px;
  margin-bottom: ${RFValue(12)}px;
`;

export function DashedLine({
  categoria,
  theme,
}: {
  categoria: string;
  theme: DefaultTheme;
}) {
  const dashes = Array.from({length: 100}, (_, i) =>
    React.createElement(View, {
      key: i,
      style: {
        width: 6,
        height: 1,
        backgroundColor: getCategoryTokens(categoria, theme).border,
        marginRight: 4,
      },
    }),
  );
  return React.createElement(
    View,
    {
      style: {
        flex: 1,
        height: 1,
        flexDirection: 'row' as const,
        overflow: 'hidden' as const,
      },
    },
    dashes,
  );
}

// top é definido via style prop com onLayout — não inclua aqui
export const NotchCircle = styled.View<{
  side: 'left' | 'right';
  categoria: string;
}>`
  width: ${RFValue(NOTCH_R * 2)}px;
  height: ${RFValue(NOTCH_R * 2)}px;
  border-radius: ${RFValue(NOTCH_R)}px;
  background-color: ${({theme}) => theme.colors.background};
  border-width: 1.5px;
  z-index: 20;
  border-color: ${({categoria, theme}) => getCategoryTokens(categoria, theme).border};
  position: absolute;
  ${({side}) =>
    side === 'left'
      ? `left: -${RFValue(NOTCH_R)}px;`
      : `right: -${RFValue(NOTCH_R)}px;`}
`;

// ─── Bottom section ───────────────────────────────────────────────────────────

export const BottomSection = styled.View`
  flex: 1;
  padding: ${RFValue(0)}px ${RFValue(16)}px ${RFValue(14)}px;
  gap: ${RFValue(12)}px;
`;

export const InfoRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: ${RFValue(12)}px 0;
`;

export const InfoItem = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${RFValue(6)}px;
`;

export const InfoTextStack = styled.View``;

/* CORRIGIDO: era color: #1a1a1a (fixo) — ficava ilegível no dark mode.
   Agora usa theme.colors.title (dark: #fff / light: #222). */
export const InfoValue = styled.Text<{urgent?: boolean}>`
  color: ${({urgent, theme}) => (urgent ? EXPIRING_TODAY_COLOR : theme.colors.title)};
  font-size: ${RFValue(14)}px;
  font-family: ${({theme}) => theme.fonts.bold};
`;

export const InfoLabel = styled.Text`
  color: #9e9e9e;
  font-size: ${RFValue(8.5)}px;
  font-family: ${({theme}) => theme.fonts.bold};
  letter-spacing: 1px;
`;

/* CORRIGIDO: era color: rgba(0, 0, 0, 0.8) (fixo) — mesmo problema no dark mode.
   Agora usa theme.colors.text_2 (dark: #FFF / light: #797e91). */
export const InfoLabelSmall = styled.Text<{urgent?: boolean}>`
  color: ${({urgent, theme}) => (urgent ? EXPIRING_TODAY_COLOR : theme.colors.text_2)};
  font-size: ${RFValue(9)}px;
  font-family: ${({theme}) => theme.fonts.semibold};
  letter-spacing: 0.5px;
  line-height: ${RFValue(11)}px;
`;

export const ActionBtn = styled.TouchableOpacity<{categoria: string}>`
  background-color: ${({categoria, theme}) =>
    getCategoryTokens(categoria, theme).actionBtnBg};
  border-radius: ${RFValue(12)}px;
  align-self: center;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  height: ${RFValue(35)}px;

  padding:0 24px;

  border-radius: ${RFValue(24)}px;

`;

export const ActionBtnIcon = styled(MaterialCommunityIcons)<{
  categoria: string;
}>`
  color: ${({categoria, theme}) => getCategoryTokens(categoria, theme).actionBtnText};
`;

export const ActionBtnLeft = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${RFValue(4)}px;
`;

export const ActionBtnText = styled.Text<{categoria: string}>`
  color: ${({categoria, theme}) => getCategoryTokens(categoria, theme).actionBtnText};
  font-size: ${RFValue(11)}px;
  font-family: ${({theme}) => theme.fonts.bold};
`;

export const TapHint = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${RFValue(5)}px;
`;

export const TapHintText = styled.Text`
  color: #b0b0b0;
  font-size: ${RFValue(10)}px;
  font-family: ${({theme}) => theme.fonts.regular};
`;

// ─── Blocked badge ────────────────────────────────────────────────────────────

export const InfoDivider = styled.View<{categoria: string}>`
  width: 1px;
  height: ${RFValue(32)}px;
  background-color: ${({categoria, theme}) => getCategoryTokens(categoria, theme).border};
`;

export const BlockedRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${RFValue(6)}px;
  padding-vertical: ${RFValue(6)}px;
`;

export const BlockedText = styled.Text`
  color: #9e9e9e;
  font-size: ${RFValue(11)}px;
  font-family: ${({theme}) => theme.fonts.bold};
  letter-spacing: 1px;
`;

// ─── Overlays ────────────────────────────────────────────────────────────────

export const LockedOverlay = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  justify-content: center;
  align-items: center;
  gap: ${RFValue(6)}px;
`;

export const LockedMsg = styled.Text`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${RFValue(12)}px;
  font-family: ${({theme}) => theme.fonts.bold};
  text-align: center;
  padding-horizontal: ${RFValue(24)}px;
`;

// Camada de fundo: apenas o escurecimento, sem conteúdo (z abaixo do conteúdo)
export const ExpiredOverlayBackground = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: ${RFValue(20)}px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 0;
`;

// Camada de conteúdo: ícone/título/botão, separada do fundo para ficar sempre por cima
export const ExpiredOverlayContent = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
  gap: ${RFValue(8)}px;
  z-index: 1;
  padding:0 24px;
`;

export const ExpiredOverlayTitle = styled.Text`
  color: ${({theme}) => theme.colors.shape};
  font-size: ${RFValue(12)}px;
  font-family: ${({theme}) => theme.fonts.bold};
  text-align: center;
`;

export const RefundBtn = styled.TouchableOpacity`
  margin-top: ${RFValue(2)}px;
  background-color: ${({theme}) => theme.colors.primary};
  border-radius: ${RFValue(20)}px;
  padding: ${RFValue(8)}px ${RFValue(16)}px;

`;

export const RefundBtnText = styled.Text`
    color: ${({theme}) => theme.colors.shape};
  font-size: ${RFValue(10)}px;
  font-family: ${({theme}) => theme.fonts.bold};

`;
